import type {
  WidgetSyncState,
  StateSyncConfig,
  SyncMessage,
  StateSyncUpdateMessage,
} from '@/types';

const INPUT_DEBOUNCE_MS = 200;

/**
 * State synchronization service for embedded/floating widget communication.
 * Uses postMessage to communicate via the parent page broker.
 */
export class StateSyncService {
  private config: StateSyncConfig;
  private inputDebounceTimer: number | null = null;
  private isReceivingSync = false;
  private lastEmittedStateHash = '';
  private boundHandleMessage: (event: MessageEvent) => void;

  constructor(config: StateSyncConfig) {
    this.config = config;
    this.boundHandleMessage = this.handleMessage.bind(this);
    this.setupListener();
  }

  /**
   * Set up postMessage listener.
   */
  private setupListener(): void {
    window.addEventListener('message', this.boundHandleMessage);
  }

  /**
   * Clean up listener and timers.
   */
  public destroy(): void {
    window.removeEventListener('message', this.boundHandleMessage);
    if (this.inputDebounceTimer !== null) {
      clearTimeout(this.inputDebounceTimer);
      this.inputDebounceTimer = null;
    }
  }

  /**
   * Handle incoming postMessage.
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin if parentOrigin is specified
    if (this.config.parentOrigin !== '*') {
      if (event.origin !== this.config.parentOrigin) {
        return;
      }
    }

    const data = event.data as SyncMessage;
    if (!data || typeof data !== 'object' || !data.type) {
      return;
    }

    // Only handle Onboardi messages
    if (!data.type.startsWith('onboardi:')) {
      return;
    }

    switch (data.type) {
      case 'onboardi:state-sync':
        this.handleStateSync(data.payload);
        break;

      case 'onboardi:visibility':
        this.config.onVisibilityChange(data.payload.action);
        break;
    }
  }

  /**
   * Handle incoming state sync from the other widget instance.
   */
  private handleStateSync(state: WidgetSyncState): void {
    // Mark that we're receiving a sync to prevent echo
    this.isReceivingSync = true;

    // Call the callback to update widget state
    this.config.onStateSync(state);

    // Reset flag after a microtask to allow state to settle
    queueMicrotask(() => {
      this.isReceivingSync = false;
    });
  }

  /**
   * Emit state update to parent page immediately.
   * Used for message list changes.
   */
  public emitStateUpdate(state: WidgetSyncState): void {
    // Don't emit if we're currently receiving a sync (prevent loop)
    if (this.isReceivingSync) {
      return;
    }

    // Create a simple hash to detect duplicate emissions
    const stateHash = this.createStateHash(state);
    if (stateHash === this.lastEmittedStateHash) {
      return;
    }
    this.lastEmittedStateHash = stateHash;

    const message: StateSyncUpdateMessage = {
      type: 'onboardi:state-update',
      payload: {
        ...state,
        instanceId: this.config.instanceId,
      },
    };

    // Post to parent window (for embedded iframe)
    // or to self (for floating widget on same page)
    const targetOrigin = this.config.parentOrigin === '*' ? '*' : this.config.parentOrigin;

    if (window.parent !== window) {
      // We're in an iframe, post to parent
      window.parent.postMessage(message, targetOrigin);
    } else {
      // We're on the main page, post to self for the orchestrator
      window.postMessage(message, targetOrigin);
    }
  }

  /**
   * Emit state update with debounce.
   * Used for input text changes to avoid excessive updates.
   */
  public emitStateUpdateDebounced(state: WidgetSyncState): void {
    if (this.isReceivingSync) {
      return;
    }

    if (this.inputDebounceTimer !== null) {
      clearTimeout(this.inputDebounceTimer);
    }

    this.inputDebounceTimer = window.setTimeout(() => {
      this.emitStateUpdate(state);
      this.inputDebounceTimer = null;
    }, INPUT_DEBOUNCE_MS);
  }

  /**
   * Create a simple hash for state comparison.
   */
  private createStateHash(state: WidgetSyncState): string {
    // Include message count and last message ID for quick comparison
    const lastMsg = state.messages[state.messages.length - 1];
    return `${state.messages.length}:${lastMsg?.id || ''}:${state.inputText}:${state.isAwaitingResponse}`;
  }

  /**
   * Update session ID (when external sessionId changes).
   */
  public updateSessionId(sessionId: string): void {
    this.config.sessionId = sessionId;
  }
}

/**
 * Factory function for creating sync service.
 */
export function createStateSyncService(config: StateSyncConfig): StateSyncService {
  return new StateSyncService(config);
}
